import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  signIn,
  confirmSignIn,
  resetPassword,
  confirmResetPassword,
} from 'aws-amplify/auth';
import { useAuth } from './AuthContext';
import {
  AuthShell,
  AuthField,
  SubmitButton,
  TextButton,
  FormError,
  FormNotice,
} from './authUi';

// UI steps in the authentication flow.
const STEP = {
  SIGN_IN: 'SIGN_IN',
  NEW_PASSWORD: 'NEW_PASSWORD',
  FORGOT_REQUEST: 'FORGOT_REQUEST',
  FORGOT_CONFIRM: 'FORGOT_CONFIRM',
};

export default function LoginGate() {
  const { refresh } = useAuth();

  const [step, setStep] = useState(STEP.SIGN_IN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');

  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const resetTransient = () => {
    setError(null);
    setNotice(null);
    setCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Route a Cognito "next step" to the matching UI screen.
  const applyNextStep = async (nextStep) => {
    switch (nextStep?.signInStep) {
      case 'DONE':
        await refresh();
        return;
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
        setStep(STEP.NEW_PASSWORD);
        return;
      case 'RESET_PASSWORD':
        setNotice('Your password must be reset. Enter your email to begin.');
        setStep(STEP.FORGOT_REQUEST);
        return;
      default:
        setError(
          'This account needs attention. Please contact your administrator.',
        );
    }
  };

  const run = async (fn) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const submitSignIn = (e) => {
    e.preventDefault();
    run(async () => {
      try {
        const { nextStep } = await signIn({
          username: email.trim().toLowerCase(),
          password,
          options: { authFlowType: 'USER_SRP_AUTH' },
        });
        setPassword('');
        await applyNextStep(nextStep);
      } catch {
        setError('Incorrect email or password.');
      }
    });
  };

  const submitNewPassword = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    run(async () => {
      try {
        const { nextStep } = await confirmSignIn({
          challengeResponse: newPassword,
        });
        resetTransient();
        await applyNextStep(nextStep);
      } catch {
        setError(
          'That password was not accepted. Use at least 8 characters with a mix of letters, numbers, and symbols.',
        );
      }
    });
  };

  const submitForgotRequest = (e) => {
    e.preventDefault();
    run(async () => {
      try {
        await resetPassword({ username: email.trim().toLowerCase() });
      } catch {
        // Swallow errors so we never reveal whether an email exists.
      }
      resetTransient();
      setNotice('If that email is registered, a reset code is on its way.');
      setStep(STEP.FORGOT_CONFIRM);
    });
  };

  const submitForgotConfirm = (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    run(async () => {
      try {
        await confirmResetPassword({
          username: email.trim().toLowerCase(),
          confirmationCode: code.trim(),
          newPassword,
        });
        resetTransient();
        setNotice('Password updated. You can now sign in.');
        setStep(STEP.SIGN_IN);
      } catch {
        setError(
          'That code was invalid or expired, or the password was too weak.',
        );
      }
    });
  };

  const goToSignIn = () => {
    resetTransient();
    setStep(STEP.SIGN_IN);
  };

  switch (step) {
    case STEP.NEW_PASSWORD:
      return (
        <AuthShell
          title="Set your password"
          subtitle="Choose a new password to finish setting up your account."
        >
          <form onSubmit={submitNewPassword} className="space-y-5">
            <AuthField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoFocus
              hint="At least 8 characters, including letters, numbers, and a symbol."
            />
            <AuthField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <SubmitButton busy={busy} busyLabel="Saving…">
              Save password
            </SubmitButton>
          </form>
          <FormError>{error}</FormError>
        </AuthShell>
      );

    case STEP.FORGOT_REQUEST:
      return (
        <AuthShell
          title="Reset your password"
          subtitle="Enter your email and we’ll send you a reset code."
        >
          <form onSubmit={submitForgotRequest} className="space-y-5">
            <AuthField
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <SubmitButton busy={busy} busyLabel="Sending…">
              Send reset code
            </SubmitButton>
          </form>
          <FormNotice>{notice}</FormNotice>
          <FormError>{error}</FormError>
          <div className="text-center mt-4">
            <TextButton onClick={goToSignIn}>Back to sign in</TextButton>
          </div>
        </AuthShell>
      );

    case STEP.FORGOT_CONFIRM:
      return (
        <AuthShell
          title="Enter reset code"
          subtitle={`We emailed a code to ${email}. Enter it with your new password.`}
        >
          <form onSubmit={submitForgotConfirm} className="space-y-5">
            <AuthField
              label="Reset code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoFocus
            />
            <AuthField
              label="New password"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
            <AuthField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <SubmitButton busy={busy} busyLabel="Updating…">
              Update password
            </SubmitButton>
          </form>
          <FormNotice>{notice}</FormNotice>
          <FormError>{error}</FormError>
          <div className="text-center mt-4">
            <TextButton onClick={goToSignIn}>Back to sign in</TextButton>
          </div>
        </AuthShell>
      );

    case STEP.SIGN_IN:
    default:
      return (
        <AuthShell
          title="Sign in to view the map"
          subtitle="Access is limited to invited members."
        >
          <form onSubmit={submitSignIn} className="space-y-5">
            <AuthField
              label="Email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
            />
            <AuthField
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <SubmitButton busy={busy} busyLabel="Signing in…">
              Sign in
            </SubmitButton>
          </form>
          <Link
            to="/contact"
            className="mt-3 flex w-full items-center justify-center rounded-xl border border-blue-500/60 bg-blue-500/10 py-3 font-medium text-blue-300 transition hover:bg-blue-500/20"
          >
            Request access
          </Link>
          <FormNotice>{notice}</FormNotice>
          <FormError>{error}</FormError>
          <div className="text-center mt-4">
            <TextButton
              onClick={() => {
                resetTransient();
                setStep(STEP.FORGOT_REQUEST);
              }}
            >
              Forgot password?
            </TextButton>
          </div>
        </AuthShell>
      );
  }
}
